"use client";

import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@/components/ui/field";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
	InputGroupText,
	InputGroupTextarea,
} from "@/components/ui/input-group";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { EmailSchema, NameSchema, cn } from "@/lib/utils";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import { HelpCircle, MailIcon, UserIcon } from "lucide-react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import * as v from "valibot";
import { Button } from "../ui/button";

const formSchema = v.object({
	name: NameSchema,
	email: EmailSchema,
	message: v.string(),
});

interface IContactFormInput {
	name: string;
	email: string;
	message: string;
}

interface ContactFormProps {
	className?: string;
}

export function ContactForm({ className }: ContactFormProps) {
	const { control, handleSubmit } = useForm<IContactFormInput>({
		resolver: valibotResolver(formSchema),
		defaultValues: {
			name: "",
			email: "",
			message: "",
		},
	});

	const onSubmit: SubmitHandler<IContactFormInput> = (data) => {
		console.log(data);
	};

	return (
		<form
			id="form-contact"
			className={cn("mx-auto w-full px-4", className)}
			onSubmit={handleSubmit(onSubmit)}
		>
			<div className="flex flex-col gap-y-6">
				<div className="flex flex-col gap-1">
					<Controller
						name="name"
						control={control}
						rules={{ required: true }}
						render={({ field, fieldState }) => (
							<Field data-invalid={fieldState.invalid}>
								<FieldLabel htmlFor="form-profile-name" className="font-bold">
									Name
								</FieldLabel>

								<InputGroup>
									<InputGroupInput
										id="form-profile-name"
										type="name"
										autoComplete="name"
										placeholder="Your name"
										aria-invalid={fieldState.invalid}
										{...field}
									/>
									<InputGroupAddon>
										<UserIcon />
									</InputGroupAddon>
									<InputGroupAddon align="inline-end">
										<Tooltip delayDuration={0}>
											<TooltipTrigger asChild>
												<HelpCircle />
											</TooltipTrigger>
											<TooltipContent>
												<TooltipArrow className="fill-primary" />
												<p>Help us with your name</p>
											</TooltipContent>
										</Tooltip>
									</InputGroupAddon>
								</InputGroup>

								{fieldState.invalid && (
									<FieldError errors={[fieldState.error]} />
								)}
							</Field>
						)}
					/>
				</div>

				<div className="flex flex-col gap-1">
					<label
						htmlFor="email"
						className="block text-sm/6 font-semibold text-primary"
					>
						Email
					</label>
					<Controller
						name="email"
						control={control}
						rules={{ required: true }}
						render={({ field, fieldState }) => (
							<Field data-invalid={fieldState.invalid}>
								<InputGroup>
									<InputGroupInput
										id="form-reset-password-email"
										type="email"
										autoComplete="email"
										placeholder="your@email.com"
										aria-invalid={fieldState.invalid}
										{...field}
									/>
									<InputGroupAddon>
										<MailIcon />
									</InputGroupAddon>
									<InputGroupAddon align="inline-end">
										<Tooltip delayDuration={0}>
											<TooltipTrigger asChild>
												<HelpCircle />
											</TooltipTrigger>
											<TooltipContent>
												<TooltipArrow className="fill-primary" />
												<p>We&apos;ll use this to contact you</p>
											</TooltipContent>
										</Tooltip>
									</InputGroupAddon>
								</InputGroup>

								{fieldState.invalid && (
									<FieldError errors={[fieldState.error]} />
								)}
							</Field>
						)}
					/>
				</div>

				<div className="flex flex-col gap-1">
					<Controller
						name="message"
						control={control}
						rules={{ required: true }}
						render={({ field, fieldState }) => (
							<Field data-invalid={fieldState.invalid}>
								<FieldLabel
									htmlFor="form-profile-description"
									className="font-bold"
								>
									Message
								</FieldLabel>

								<InputGroup>
									<InputGroupTextarea
										{...field}
										id="form-profile-description"
										placeholder="Tell us more about your inquiry..."
										rows={6}
										className="min-h-24 resize-none"
										aria-invalid={fieldState.invalid}
									/>
									<InputGroupAddon align="block-end">
										<InputGroupText className="tabular-nums">
											{field.value.length}/1000 characters
										</InputGroupText>
									</InputGroupAddon>
								</InputGroup>
								<FieldDescription>
									Tell us more about your inquiry. This will help us respond to
									you better.
								</FieldDescription>

								{fieldState.invalid && (
									<FieldError errors={[fieldState.error]} />
								)}
							</Field>
						)}
					/>
				</div>

				<div className="flex gap-x-4 items-center justify-start">
					<div className="flex h-6 items-center">
						<div className="group relative inline-flex w-8 shrink-0 rounded-full bg-primary/5 p-px outline-offset-2 outline-primary ring-1 ring-inset ring-white/10 transition-colors duration-200 ease-in-out has-[:checked]:bg-primary has-[:focus-visible]:outline has-[:focus-visible]:outline-2">
							<span className="size-4 rounded-full bg-white shadow-sm ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out group-has-[:checked]:translate-x-3.5" />
							<input
								id="agree-to-policies"
								name="agree-to-policies"
								type="checkbox"
								aria-label="Agree to policies"
								className="absolute inset-0 appearance-none focus:outline-none"
							/>
						</div>
					</div>
					<label
						htmlFor="agree-to-policies"
						className="text-xs md:text-sm text-primary"
					>
						Agree to our{" "}
						<a
							href="/privacy-policy"
							className="whitespace-nowrap font-semibold text-primary"
						>
							privacy policy
						</a>
						.
					</label>
				</div>
			</div>
			<div className="mt-6">
				<Button
					size="default"
					type="submit"
					className="w-full"
					form="form-contact"
				>
					Submit
				</Button>
			</div>
		</form>
	);
}
